<template>
  <div class="py-4">
    <v-card class="mx-auto" max-width="1200">
      <v-toolbar color="primary" dark>
        <v-toolbar-title>대시보드</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="loadDashboardData">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0">
        <v-list dense>
          <!-- 서버 헬스 상태 -->
          <v-list-item class="grey lighten-3">
            <v-list-item-content class="text-center">
              <strong>[ 서버 헬스 상태 ]</strong>
            </v-list-item-content>
          </v-list-item>
          
          <v-container fluid class="pa-0">
            <v-row no-gutters>
              <v-col 
                v-for="(server, index) in serverHealthList" 
                :key="index" 
                cols="12" 
                md="6"
              >
                <v-card 
                  flat 
                  :color="getServerColor(server.key || server.name)" 
                  class="ma-1 pa-3"
                >
                  <div class="d-flex justify-space-between align-center">
                    <strong>{{ server.name }}</strong>
                    <div class="text-right">
                      <span>{{ getHealthStatusText(server.status) }}</span>
                      <span v-if="server.elapsed" class="ml-2">
                        (지연속도: {{ server.elapsed }}ms)
                      </span>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-container>

          <v-divider class="my-4"></v-divider>

          <!-- HTTPS 인증서 상태 -->
          <v-list-item class="grey lighten-3">
            <v-list-item-content class="text-center">
              <strong>[ HTTPS 인증서 만료일 ]</strong>
            </v-list-item-content>
          </v-list-item>

          <v-container fluid class="pa-0">
            <v-row no-gutters>
              <v-col 
                v-for="(cert, index) in certificateList" 
                :key="index" 
                cols="12" 
                md="6"
                class="mb-3"
              >
                <v-card flat class="ma-1 pa-3" outlined>
                  <div class="mb-2">
                    <strong>{{ cert.server_name }}</strong>
                    <span v-if="cert.merged_count > 1" class="text-caption grey--text ml-1">
                      (인증서 {{ cert.merged_count }}개 중 가장 빠른 만료 기준)
                    </span>
                  </div>
                  <div class="mb-2">
                    <v-progress-linear
                      :value="cert.progress"
                      :color="getCertificateColor(cert.days_remaining)"
                      height="25"
                      rounded
                    >
                      <template v-slot:default="{ value }">
                        <strong class="white--text">
                          {{ cert.days_remaining }}일 남음
                        </strong>
                      </template>
                    </v-progress-linear>
                  </div>
                  <div class="text-caption grey--text">
                    만료일: {{ cert.expiry_date }}
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-list>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import store from '../store';
import moment from 'moment';

export default {
  name: 'DashBoard',
  
  data: () => ({
    loadingData: false,
    dashboardData: {},
    serverHealthList: [],
    certificateList: []
  }),

  created() {
    this.loadDashboardData();
  },

  methods: {
    ...mapActions([
      'GET_DASHBOARD_HEALTH_DATA'
    ]),

    loadDashboardData() {
      this.loadingData = true;
      this.GET_DASHBOARD_HEALTH_DATA({}).then((body_data) => {
        this.loadingData = false;
        
        // 디버깅: 응답 구조 확인
        console.log('📥 전체 응답 데이터:', JSON.stringify(body_data, null, 2));
        
        // 백엔드 응답: {dashboard_data: dashboard_health_data}
        // 또는 {response_data: {dashboard_data: dashboard_health_data}}
        this.dashboardData = body_data.dashboard_data || body_data.response_data?.dashboard_data || body_data || {};
        
        console.log('📊 파싱된 dashboardData:', JSON.stringify(this.dashboardData, null, 2));
        
        // 서버 헬스 상태 파싱
        this.parseServerHealth();
        
        // HTTPS 인증서 정보 파싱
        this.parseCertificates();
      })
      .catch((err) => {
        this.loadingData = false;
        console.error('대시보드 데이터 로드 실패:', err);
      });
    },

    parseServerHealth() {
      // 백엔드에서 받은 헬스 체크 데이터를 파싱
      // health_check_cpu_db, health_check_cpu_api 등으로 직접 오는 구조
      this.serverHealthList = [];

      console.log('🏥 전체 dashboardData:', JSON.stringify(this.dashboardData, null, 2));

      // 각 서버별 헬스 체크 정보 추가
      const serverMappings = [
        { key: 'health_check_cpu_api', name: '채굴기 API 서버' },
        { key: 'health_check_cpu_db', name: '채굴기 DB 서버' },
        { key: 'health_check_snapquiz_api', name: '퀴즈 API 서버' },
        { key: 'health_check_snapquiz_db', name: '퀴즈 DB 서버' },
        { key: 'health_check_carrygold_db', name: '금털기 DB 서버' },
        { key: 'health_check_carrygold_api', name: '금털기 API 서버' },
        { key: 'health_check_quizcash_api', name: '캐시퀴즈 API 서버' },
        { key: 'health_check_admin_api', name: '오퍼월 API 서버' }
      ];

      serverMappings.forEach(mapping => {
        const serverData = this.dashboardData[mapping.key];
        if (serverData) {
          this.serverHealthList.push({
            name: mapping.name,
            key: mapping.key,
            status: serverData.status,
            elapsed: serverData.elapsed
          });
        }
      });

      console.log('✅ 파싱된 서버 헬스 리스트:', this.serverHealthList);
    },

    parseCertificates() {
      // 백엔드에서 받은 인증서 데이터를 파싱
      // certbot_status_list 배열로 오고, 각 항목의 value가 JSON 문자열
      const certData = this.dashboardData.certbot_status_list || [];
      this.certificateList = [];

      console.log('🔒 인증서 원본 데이터:', JSON.stringify(certData, null, 2));

      if (Array.isArray(certData) && certData.length > 0) {
        certData.forEach(item => {
          try {
            // value가 JSON 문자열이므로 파싱
            const cert = typeof item.value === 'string' ? JSON.parse(item.value) : item.value;
            
            // 에러가 있는 경우 스킵
            if (cert.error) {
              console.warn('인증서 에러 항목:', cert);
              return;
            }

            // daysRemaining이 이미 계산되어 있으면 사용, 없으면 expiryDate로 계산
            const daysRemaining = cert.daysRemaining !== undefined 
              ? cert.daysRemaining 
              : this.calculateDaysRemaining(cert.expiryDate || cert.expiry_date);
            
            const progress = this.calculateProgress(daysRemaining);
            
            this.certificateList.push({
              server_name: cert.serverName || cert.server_name || cert.domain || '알 수 없음',
              expiry_date: cert.expiryDate || cert.expiry_date || '알 수 없음',
              days_remaining: daysRemaining,
              progress: progress,
              status: cert.status || 'unknown'
            });
          } catch (e) {
            console.error('인증서 데이터 파싱 실패:', item, e);
          }
        });
      }

      // 같은 서버명은 하나로 묶고, 남은 일수가 가장 적은(가장 먼저 만료되는) 인증서 기준으로 표시
      const byName = {};
      const order = [];
      this.certificateList.forEach((cert) => {
        const prev = byName[cert.server_name];
        if (!prev) {
          byName[cert.server_name] = { ...cert, merged_count: 1 };
          order.push(cert.server_name);
          return;
        }
        prev.merged_count += 1;
        if (Number(cert.days_remaining) < Number(prev.days_remaining)) {
          Object.assign(prev, cert, { merged_count: prev.merged_count });
        }
      });
      this.certificateList = order.map((name) => byName[name]);

      console.log('✅ 파싱된 인증서 리스트:', this.certificateList);
    },

    calculateDaysRemaining(expiryDate) {
      if (!expiryDate) return 0;
      
      try {
        const expiry = moment(expiryDate);
        const now = moment();
        const days = expiry.diff(now, 'days');
        return days > 0 ? days : 0;
      } catch (e) {
        console.error('날짜 계산 실패:', e);
        return 0;
      }
    },

    calculateProgress(daysRemaining) {
      // 90일을 100%로 기준
      const maxDays = 90;
      if (daysRemaining >= maxDays) return 100;
      return Math.max(0, (daysRemaining / maxDays) * 100);
    },

    getServerColor(serverKey) {
      // 서버별 색상 매핑
      const colorMap = {
        'health_check_cpu_api': '#E3F2FD',      // 채굴기 API - 밝은 파란색
        'health_check_cpu_db': '#E3F2FD',        // 채굴기 DB - 중간 파란색
        'health_check_snapquiz_api': '#F3E5F5', // 퀴즈 API - 밝은 보라색
        'health_check_snapquiz_db': '#F3E5F5',  // 퀴즈 DB - 중간 보라색
        'health_check_quizcash_api': '#E8F5E9', // 캐시퀴즈 API - 밝은 초록색
        'health_check_quizcash_db': '#E8F5E9',  // 캐시퀴즈 DB - 중간 초록색
        'health_check_carrygold_api': '#FFF8E1', // 금털기 API - 밝은 골드색
        'health_check_carrygold_db': '#FFF8E1',  // 금털기 DB - 밝은 골드색
        'health_check_admin_api': '#FFF3E0'     // 오퍼월 API - 밝은 주황색
      };
      
      // 서버 이름으로도 매핑 시도
      const nameMap = {
        '채굴기 API 서버': '#E3F2FD',
        '채굴기 DB 서버': '#E3F2FD',
        '퀴즈 API 서버': '#F3E5F5',
        '퀴즈 DB 서버': '#F3E5F5',
        '캐시퀴즈 API 서버': '#E8F5E9',
        '캐시퀴즈 DB 서버': '#E8F5E9',
        '금털기 API 서버': '#FFF8E1',
        '금털기 DB 서버': '#FFF8E1',
        '오퍼월 API 서버': '#FFF3E0'
      };
      
      return colorMap[serverKey] || nameMap[serverKey] || '#F5F5F5';
    },

    getHealthColor(status) {
      return status === 'ok' ? '#E3F2FD' : '#FF6C6C';
    },

    getHealthStatusText(status) {
      return status === 'ok' ? '쾌적' : '불안정';
    },

    getCertificateColor(daysRemaining) {
      if (daysRemaining >= 60) {
        return 'green'; // 60~90일: 녹색
      } else if (daysRemaining >= 30) {
        return 'orange'; // 30~60일: 노란색
      } else {
        return 'red'; // 30일 미만: 빨간색
      }
    }
  }
};
</script>
